import { PublicClientApplication, BrowserAuthError } from "@azure/msal-browser";
import type { Configuration } from "@azure/msal-browser";

export type AppKey = "aidenvision" | "aidenbot";

export interface ConsentResult {
  app: AppKey;
  tenantId: string;
  username: string;
}

const AIDENVISION_CLIENT_ID = process.env.REACT_APP_CONSENT_AIDENVISION_CLIENT_ID ?? "";
const AIDENBOT_CLIENT_ID = process.env.REACT_APP_CONSENT_AIDENBOT_CLIENT_ID ?? "";

const APP_SCOPES: Record<AppKey, string[]> = {
  aidenvision: ["User.Read", "email"],
  aidenbot: AIDENBOT_CLIENT_ID
    ? [`api://${AIDENBOT_CLIENT_ID}/OS.Deploy`]
    : []
};

function clientIdFor(app: AppKey): string {
  return app === "aidenvision" ? AIDENVISION_CLIENT_ID : AIDENBOT_CLIENT_ID;
}

function buildMsalConfig(clientId: string): Configuration {
  return {
    auth: {
      clientId,
      authority: "https://login.microsoftonline.com/common",
      redirectUri: window.location.origin + "/auth-response.html"
    },
    cache: {
      cacheLocation: "sessionStorage"
    }
  };
}

const instances = new Map<AppKey, PublicClientApplication>();

function getInstance(app: AppKey): PublicClientApplication {
  const cached = instances.get(app);
  if (cached) return cached;

  const clientId = clientIdFor(app);
  if (!clientId) {
    throw new ConsentError(
      `Missing client id for ${app}. Check env vars.`,
      "missing_config"
    );
  }

  const msal = new PublicClientApplication(buildMsalConfig(clientId));
  instances.set(app, msal);
  return msal;
}

export class ConsentError extends Error {
  readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = "ConsentError";
    this.code = code;
  }
}

export async function requestAdminConsent(app: AppKey): Promise<ConsentResult> {
  const msal = getInstance(app);
  const scopes = APP_SCOPES[app];

  try {
    const loginResponse = await msal.loginPopup({ scopes });
    if (!loginResponse.account) {
      throw new ConsentError("No account returned by Entra.", "no_account");
    }

    return {
      app,
      tenantId: loginResponse.tenantId ?? loginResponse.account.tenantId,
      username: loginResponse.account.username
    };
  } catch (err) {
    throw normalizeAuthError(err);
  }
}

function normalizeAuthError(err: unknown): ConsentError {
  if (err instanceof BrowserAuthError) {
    if (err.errorCode === "user_cancelled") {
      return new ConsentError("Consent was cancelled.", "cancelled");
    }
    if (err.errorCode === "popup_window_error") {
      return new ConsentError(
        "Popup blocked. Allow popups for this site and try again.",
        "popup_blocked"
      );
    }
    return new ConsentError(err.errorMessage || err.message, err.errorCode);
  }

  if (err instanceof ConsentError) return err;

  if (err instanceof Error) {
    return new ConsentError(err.message, "unknown_error");
  }

  return new ConsentError("Unexpected error during consent.", "unknown_error");
}
